import {css, StyleSheet} from 'aphrodite';
import React, {lazy, Suspense} from 'react';

import AutoSizer from 'components/AutoSizer';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

/**
 * Base config to insert in all Plotly charts.
 *
 * Plotly comes with some features that we chose to disable because they don't fit well in our design systems. Mainly the modebar.
 * We could re-enable in the future if we'd like. If we do so, we should take care to update icons, styles, wrapper components, ect.
 * to match our design system, and we should consider our customer's future expectation around keeping these features around in
 * perpetuity incase we decide to change charting systems again.
 *
 * This isn't typed with type from the Plotly package because it's lazy loaded at runtime.
 *
 * @package
 */
export const BASE_PLOTLY_CONFIG = {
  displayModeBar: false,
};

export interface PlotlyChartProps {
  libraryConfig: JSONScalar;
  /**
   * Called when a chart is unloaded, due to browser resource limitations.
   *
   * Charts that use WebGL may be unloaded if the browser reaches its maximum number of  WebGL contexts.
   */
  onChartForceUnloaded?: () => void;
}

// We lazy load Plotly.js at runtime because including it in the bundle causes a series of issues: On definition, Plotly.js calls
// window.URL.createObjectURL(). This function is not available in our tests and window isn't immediately available in our `NodeEnv`
// bundle. The former makes tests crash and the latter breaks Entry exports completely.
const PlotlyPlot = lazy(() => import('react-plotly.js'));

/**
 * Render a `Chart` that has `PLOTLY` as a `library`.
 *
 * This comoponent shouldn't be used directly, instead use `ChartContent`
 *
 * This component should render the given chart as-is without modifying either the `data` or `layout` properties. This is because
 * we expect the `Chart` to be generated on the backend so that the chart can be stored as a permanent snapshot of what was rendered
 * at the time to maintain ALCOA principles with an interactive chart. If we make any modifications beyond the most basic defined
 * currently, we might introduce a deviation to that goal, where a code change here could change how the chart is rendered.x
 *
 * @package
 * @returns
 */
export default function PlotlyChart({libraryConfig, onChartForceUnloaded}: PlotlyChartProps): JSX.Element {
  return (
    <div className={css(styles.plotContainer)} {...getTestProps({component: 'PlotlyChart', element: 'root'})}>
      <AutoSizer>
        {({height, width}) => (
          <Suspense
            fallback={
              <div style={{width, height}}>
                <CenteredHorizontalThrobber />
              </div>
            }
          >
            <PlotlyPlot
              // Plotly mutates its input as state and the React Plotly package doesn't block that from mutating its props. Because the chart
              // might be stored in an immutable store like Apollo, this might cause Plotly to fail when it tries to mutate the config, so we
              // do a deep copy of everything before giving it to Plotly.

              // Don't modify `libraryConfig.data`, see docstring above why.
              data={structuredClone(libraryConfig.data)}
              // Don't modify `libraryConfig.layout`, see docstring above why.
              layout={{...structuredClone(libraryConfig.layout), width, height}}
              config={BASE_PLOTLY_CONFIG}
              onWebGlContextLost={() => {
                onChartForceUnloaded?.();
              }}
            />
          </Suspense>
        )}
      </AutoSizer>
    </div>
  );
}

const styles = StyleSheet.create({
  // Putting `PlotlyChart` in a container that's also responsive to its container's size (i.e. `ChartContent`) causes an infinite loop with
  // `autoSizer` recalculating itself and the parent container becoming slightly too big or small, which triggers `autoSizer` to recalculate
  // itself. The results is the chart continuously grows and shrinks by a small amount causing it to "jiggle". The best workaround is to make
  // `PlotlyChart` slightly smaller than the parent container's size, and this makes it not continuously re-evaluate itself.
  plotContainer: {
    width: '98%',
    height: '98%',
  },
});
